import { useCrmStore } from "@/lib/zustand/useStore";
import { ErrorComponentProps } from "..";
import { useGetTable } from "@/features/crm_system/hooks/useQuery/airtable/useGetTable/useGetTable";
import { AirtableCrmConfig, AirtableCrmProjectConfig } from "@/types/crm/airtable/config";
import { z } from "zod";
import { findCloseMatchAndSort } from "@/lib/utils/stringMatcher/findCloseMatchAndSort";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AirtableNoMatchColumnError, AirtableNoMatchTableError } from "@/lib/export/crm/types";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import { useGetTablesList } from "@/features/crm_system/hooks/useQuery/airtable/useGetTablesList";
import { useUpdateQuestion } from "@/features/question/hooks/useMutation/useUpdateQuestion";

export default function NoMatchError({ field, error }: ErrorComponentProps) {

    return (
        <div className="">
            <p className="pl-2 border-l-4 border-l-neutral-300">{error.message}</p>
            {(() => {
                if (error.code === 'no_match') {
                    if (error.ctx.field === 'table') {
                        return <NoMatchTable {...{ field, error }} />
                    }
                    if (error.ctx.field === 'column') {
                        return <NoMatchColumn {...{ field, error: error } as ErrorComponentProps<AirtableNoMatchColumnError>} />
                    }
                }
            })()}
        </div>
    )
}

function NoMatchTable({ error, field }: ErrorComponentProps<AirtableNoMatchTableError>) {
    const { crmConfig, crmProjectConfig } = useCrmStore(state => state);
    const { data: tables, isLoading, isError } = useGetTablesList({
        crmConfig: crmConfig?.config as AirtableCrmConfig,
        crmProjectConfig: crmProjectConfig as AirtableCrmProjectConfig || undefined
    });
    const closeMatchTableNames = findCloseMatchAndSort(field.data as string, tables?.tables.map(t=>t.name) || []).splice(0, 5);
    const schema = z.object({
        name: z.string(),
    })
    type schemaType = z.infer<typeof schema>
    const form = useForm<schemaType>({
        resolver: zodResolver(schema),
    })
    const { mutateAsync: editQuestion, isPending } = useUpdateQuestion()
    const onSubmit = async (data: schemaType) => {
        await editQuestion(
            {
                question_id: error.ctx.crmConfigId,
                data: {
                    crm_field_name: data.name
                }
            })
            .then(() => {
                SuccessPopup("Column name updated successfully!")
            })
            .catch((err) => {
                ErrorPopup(`Failed to update column name!~${err.message}`)
            })
    }
    return (
        <Form {...form}>
            <p className="p-2 my-4 text-red-700 rounded-lg bg-red-500/40">
                Remember that changing this table name will affect edited crm config globally, not only during export!
            </p>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                    control={form.control}
                    name='name'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{AirtableCrmConfigDictionary.table.name}</FormLabel>
                            <FormControl>
                                <div className="flex flex-col gap-2">
                                    <div className="flex">
                                        <Input {...field} />
                                        <Button type='submit' className="ml-2" disabled={isPending}>Update</Button>
                                    </div>
                                    <FormMessage />
                                    <p>Closest matches from Airtable:</p>
                                    {isLoading && <p>Loading...</p>}
                                    {isError && <p>Error fetching table names</p>}
                                    <ul>
                                        {closeMatchTableNames.map((table, i) => (
                                            <li key={i}>
                                                <Button
                                                    variant='link'
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        field.onChange(table.member)
                                                    }}
                                                    className="p-1 h-min"
                                                >{table.member}</Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </FormControl>
                        </FormItem>
                    )}
                />
            </form>
        </Form>
    )
}

function NoMatchColumn({ error, field }: ErrorComponentProps<AirtableNoMatchColumnError>) {
    const { crmConfig, crmProjectConfig } = useCrmStore(state => state);
    const { data: table, isLoading, isError } = useGetTable({
        crmConfig: crmConfig?.config as AirtableCrmConfig,
        crmProjectConfig: crmProjectConfig as AirtableCrmProjectConfig || undefined
    });
    const closeMatchColumnNames = findCloseMatchAndSort(field.data as string, table?.fields.map(f => f.name) || []).splice(0, 5);
    const schema = z.object({
        name: z.string(),
    })
    type schemaType = z.infer<typeof schema>
    const form = useForm<schemaType>({
        resolver: zodResolver(schema),
    })
    const { mutateAsync: editQuestion, isPending } = useUpdateQuestion()
    const onSubmit = async (data: schemaType) => {
        await editQuestion(
            {
                question_id: error.ctx.questionId,
                data: {
                    crm_field_name: data.name
                }
            })
            .then(() => {
                SuccessPopup("Column name updated successfully!")
            })
            .catch((err) => {
                ErrorPopup(`Failed to update column name!~${err.message}`)
            })
    }
    return (
        <Form {...form}>
            <p className="p-2 my-4 text-red-700 rounded-lg bg-red-500/40">
                Remember that changing this column name will affect edited question globally, not only during export!
            </p>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                    control={form.control}
                    name='name'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Column Name</FormLabel>
                            <FormControl>
                                <div className="flex flex-col gap-2">
                                    <div className="flex">
                                        <Input {...field} />
                                        <Button type='submit' className="ml-2" disabled={isPending}>Update</Button>
                                    </div>
                                    <FormMessage />
                                    <p>Closest matches from Airtable:</p>
                                    {isLoading && <p>Loading...</p>}
                                    {isError && <p>Error fetching column names</p>}
                                    <ul>
                                        {closeMatchColumnNames.map((column, i) => (
                                            <li key={i}>
                                                <Button
                                                    variant='link'
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        field.onChange(column.member)
                                                    }}
                                                    className="p-1 h-min"
                                                >{column.member}</Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </FormControl>
                        </FormItem>
                    )}
                />
            </form>
        </Form>
    )
}